import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useRuns } from '../hooks/useRuns';
import { getRunDateString, getWeekNumber } from '../utils/dateUtils';
import { getMilestones } from '../utils/milestoneUtils';
import { TARGET_KM, START_DATE, TARGET_DATE } from '../constants';

const WEEKDAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const Card = ({ label, value, sub }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  }}>
    <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: 'var(--primary, #2c3e50)' }}>{value}</div>
    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#555' }}>{label}</div>
    {sub && <div style={{ fontSize: '0.8rem', color: '#888' }}>{sub}</div>}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '32px' }}>
    <h2 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '8px', marginBottom: '16px', color: 'var(--primary, #2c3e50)' }}>
      {title}
    </h2>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
      {children}
    </div>
  </div>
);

const Stats = () => {
  const { runs, fetchRuns } = useRuns();

  useEffect(() => {
    fetchRuns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    if (!runs.length) return null;

    // --- Gesamtzahlen ---
    const totalKm = runs.reduce((sum, r) => sum + Number(r.kilometers), 0);
    const totalRuns = runs.length;
    const avgKm = totalKm / totalRuns;

    // --- Längster Lauf ---
    const longestRun = runs.reduce((max, r) =>
      Number(r.kilometers) > Number(max.kilometers) ? r : max
    );

    // --- Pro Läufer ---
    const runnerMap = runs.reduce((acc, r) => {
      if (!acc[r.name]) acc[r.name] = { km: 0, count: 0, weeks: new Set() };
      acc[r.name].km += Number(r.kilometers);
      acc[r.name].count++;
      const dateStr = getRunDateString(r.date);
      if (dateStr) acc[r.name].weeks.add(getWeekNumber(dateStr));
      return acc;
    }, {});

    const runners = Object.entries(runnerMap).map(([name, d]) => ({
      name,
      km: d.km,
      count: d.count,
      weeks: d.weeks.size
    }));

    const uniqueRunners = runners.length;
    const topKmRunner = [...runners].sort((a, b) => b.km - a.km)[0];
    const mostRunsRunner = [...runners].sort((a, b) => b.count - a.count)[0];
    const mostConsistentRunner = [...runners].sort((a, b) => b.weeks - a.weeks)[0];

    // --- Beliebtester Wochentag ---
    const weekdayCounts = runs.reduce((acc, r) => {
      const dateStr = getRunDateString(r.date);
      if (!dateStr) return acc;
      const day = new Date(dateStr).getDay();
      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {});
    const [popularDayIndex, popularDayCount] = Object.entries(weekdayCounts)
      .sort(([, a], [, b]) => b - a)[0] || [null, 0];

    // --- Aktivste Woche ---
    const weekKm = runs.reduce((acc, r) => {
      const dateStr = getRunDateString(r.date);
      if (!dateStr) return acc;
      const week = getWeekNumber(dateStr);
      acc[week] = (acc[week] || 0) + Number(r.kilometers);
      return acc;
    }, {});
    const [busiestWeekKey, busiestWeekKm] = Object.entries(weekKm)
      .sort(([, a], [, b]) => b - a)[0] || [null, 0];
    const busiestWeekLabel = busiestWeekKey
      ? `KW ${busiestWeekKey.split('-W')[1]}`
      : '–';

    // --- Teamdurchschnitt pro Woche ---
    const totalWeeks = Object.keys(weekKm).length;
    const avgKmPerWeek = totalWeeks > 0 ? totalKm / totalWeeks : 0;

    // --- Meilensteine ---
    const bronzeCount = runners.filter(r => r.km >= 50).length;
    const silverCount = runners.filter(r => r.km >= 100).length;
    const goldCount = runners.filter(r => r.km >= 200).length;

    // --- Fortschritt ---
    const progressPct = Math.min((totalKm / TARGET_KM) * 100, 100);

    // --- Laufzeitraum ---
    const validDates = runs
      .map(r => getRunDateString(r.date))
      .filter(Boolean)
      .sort();
    const firstRunDate = validDates[0]
      ? new Date(validDates[0]).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
      : '–';

    return {
      totalKm, totalRuns, avgKm, uniqueRunners, progressPct,
      longestRun,
      topKmRunner, mostRunsRunner, mostConsistentRunner,
      popularDay: popularDayIndex !== null ? { name: WEEKDAY_NAMES[popularDayIndex], count: popularDayCount } : null,
      busiestWeekLabel, busiestWeekKm,
      avgKmPerWeek, totalWeeks,
      bronzeCount, silverCount, goldCount,
      firstRunDate
    };
  }, [runs]);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <Link to="/" style={{ textDecoration: 'none', color: '#666', fontSize: '0.9rem' }}>
          ← Zurück
        </Link>
        <h1 style={{ margin: 0, color: 'var(--primary, #2c3e50)' }}>Saisonstatistik</h1>
      </div>

      {!stats ? (
        <p style={{ color: '#666' }}>Lädt Daten…</p>
      ) : (
        <>
          <Section title="Gesamtüberblick">
            <Card label="Gesamtkilometer" value={`${stats.totalKm.toFixed(1)} km`} sub={`${stats.progressPct.toFixed(1)}% des Ziels`} />
            <Card label="Anzahl Läufe" value={stats.totalRuns} />
            <Card label="Ø pro Lauf" value={`${stats.avgKm.toFixed(1)} km`} />
            <Card label="Aktive Läufer" value={stats.uniqueRunners} />
            <Card label="Erster Lauf" value={stats.firstRunDate} />
            <Card label="Aktive Wochen" value={stats.totalWeeks} sub={`Ø ${stats.avgKmPerWeek.toFixed(1)} km/Woche`} />
          </Section>

          <Section title="Rekorde">
            <Card
              label="Längster Lauf"
              value={`${Number(stats.longestRun.kilometers).toFixed(1)} km`}
              sub={stats.longestRun.name}
            />
            <Card
              label="Aktivste Woche"
              value={stats.busiestWeekLabel}
              sub={`${stats.busiestWeekKm.toFixed(1)} km`}
            />
            <Card
              label="Beliebtester Tag"
              value={stats.popularDay?.name ?? '–'}
              sub={stats.popularDay ? `${stats.popularDay.count} Läufe` : ''}
            />
          </Section>

          <Section title="Läufer-Highlights">
            <Card
              label="Meiste Kilometer"
              value={stats.topKmRunner.name}
              sub={`${stats.topKmRunner.km.toFixed(1)} km`}
            />
            <Card
              label="Meiste Läufe"
              value={stats.mostRunsRunner.name}
              sub={`${stats.mostRunsRunner.count} Läufe`}
            />
            <Card
              label="Konstantester Läufer"
              value={stats.mostConsistentRunner.name}
              sub={`${stats.mostConsistentRunner.weeks} Wochen aktiv`}
            />
          </Section>

          <Section title="Meilenstein-Badges">
            <Card label="Bronze (≥50 km)" value={`${stats.bronzeCount} Läufer`} />
            <Card label="Silber (≥100 km)" value={`${stats.silverCount} Läufer`} />
            <Card label="Gold (≥200 km)" value={`${stats.goldCount} Läufer`} />
          </Section>
        </>
      )}
    </div>
  );
};

export default Stats;
